import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

const SortContext = createContext();

export const useSortContext = () => {
  const context = useContext(SortContext);
  if (!context) {
    throw new Error('useSortContext must be used within a SortProvider');
  }
  return context;
};

export const SortProvider = ({ children }) => {
    // Sort state
  const [sortSettings, setSortSettings] = useState({
    // League locks visibility
    showLeagueLocksGroup: true,

    // League locks sorting
    sortLeagueLocksByLockTime: true, // true = sort by lock time, false = use page sort

    // Grouping options
    groupBy: 'day', // 'day', 'conference', 'league', 'none'

    // Sorting options
    sortBy: 'kickoff', // 'kickoff', 'chronological', 'teamRanking', 'spreadSize', 'totalPoints', 'homeTeam', 'awayTeam'
    sortDirection: 'asc', // 'asc', 'desc'

    // Group collapse state
    collapsedGroups: {} // Object to track which groups are collapsed
  });

  // Flag to prevent saving during initial load
  const [isInitialized, setIsInitialized] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('lotw-sort-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSortSettings(prev => ({
          ...prev,
          ...parsed
        }));
      } catch (error) {
        console.error('Error loading sort settings from localStorage:', error);
      }
    }
    setIsInitialized(true);
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem('lotw-sort-settings', JSON.stringify(sortSettings));
      } catch (error) {
        console.error('Error saving sort settings to localStorage:', error);
      }
    }
  }, [sortSettings, isInitialized]);

  // Update a single setting
  const updateSortSetting = useCallback((settingName, value) => {
    setSortSettings(prev => ({
      ...prev,
      [settingName]: value
    }));
  }, []);

  // Update multiple settings at once
  const updateSortSettings = useCallback((newSettings) => {
    setSortSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  }, []);

  // Reset to defaults
  const resetSortSettings = useCallback(() => {
    setSortSettings({
      showLeagueLocksGroup: true,
      sortLeagueLocksByLockTime: true,
      groupBy: 'day',
      sortBy: 'kickoff',
      sortDirection: 'asc',
      collapsedGroups: {}
    });
  }, []);

  // Check if any non-default settings are active
  const hasCustomSortSettings = useMemo(() => {
    return (
      !sortSettings.showLeagueLocksGroup ||
      !sortSettings.sortLeagueLocksByLockTime ||
      sortSettings.groupBy !== 'day' ||
      sortSettings.sortBy !== 'kickoff' ||
      sortSettings.sortDirection !== 'asc' ||
      Object.keys(sortSettings.collapsedGroups).length > 0
    );
  }, [sortSettings]);

  // Helper functions for group collapse state
  const toggleGroupCollapse = useCallback((groupKey) => {
    setSortSettings(prev => ({
      ...prev,
      collapsedGroups: {
        ...prev.collapsedGroups,
        [groupKey]: !prev.collapsedGroups[groupKey]
      }
    }));
  }, []);

  const isGroupCollapsed = useCallback((groupKey) => {
    return sortSettings.collapsedGroups[groupKey] === true;
  }, [sortSettings.collapsedGroups]);

  const expandAllGroups = useCallback(() => {
    setSortSettings(prev => ({
      ...prev,
      collapsedGroups: {}
    }));
  }, []);

  const collapseAllGroups = useCallback(() => {
    // This would need to be called with the current group keys
    // For now, we'll implement this when we have access to the groups
  }, []);

  const value = useMemo(() => ({
    sortSettings,
    updateSortSetting,
    updateSortSettings,
    resetSortSettings,
    hasCustomSortSettings,
    toggleGroupCollapse,
    isGroupCollapsed,
    expandAllGroups,
    collapseAllGroups
  }), [sortSettings, updateSortSetting, updateSortSettings, resetSortSettings, hasCustomSortSettings, toggleGroupCollapse, isGroupCollapsed, expandAllGroups, collapseAllGroups]);

  return (
    <SortContext.Provider value={value}>
      {children}
    </SortContext.Provider>
  );
};

SortProvider.propTypes = {
  children: PropTypes.node.isRequired
};
